const BUILD_VERSION = "5252026";
const PRELOAD_STORAGE_PREFIX = `plutonic-unity-preloaded:${BUILD_VERSION}:`;
const UNITY_CACHE_NAME = `plutonic-unity-webgl-${BUILD_VERSION}`;
const UNITY_DATABASE_NAME = `plutonic-unity-webgl-${BUILD_VERSION}`;
const UNITY_DATABASE_VERSION = 1;
const UNITY_ASSET_STORE = "assets";
const UNITY_CHUNK_STORE = "chunks";
const UNITY_CHUNK_SIZE = 4 * 1024 * 1024;
let serviceWorkerReady;
let persistentStorageRequested;
let unityDatabaseReady;

const PRODUCTION_ROOM_PATHS = {
  beach: "/beach",
  mountain: "/forest",
  park: "/park",
  campsite: "/campsite",
  "winter-holiday": "/winter-holiday",
  "beach-ai-companion": "/demo",
};

const DEVELOPMENT_ROOM_PATHS = {
  beach: "/devenv-plutonictherapy/beach-dev",
  mountain: "/devenv-plutonictherapy/forest-dev",
  park: "/devenv-plutonictherapy/park-dev",
  campsite: "/devenv-plutonictherapy/campsite-dev",
  "winter-holiday": "/devenv-plutonictherapy/winter-holiday-dev",
  "beach-ai-companion": "/devenv-ai-plutonictherapy/beach-ai-dev",
};

export function getUnityRoomBaseUrl(roomId) {
  if (window.location.hostname === "plutonichealth.co") {
    return new URL(PRODUCTION_ROOM_PATHS[roomId], window.location.origin).href.replace(/\/$/, "");
  }

  const isFeatureHost = window.location.hostname === "featureenv.plutonichealth.co";
  const origin = isFeatureHost
    ? window.location.origin
    : roomId === "beach-ai-companion"
    ? "https://featureenv.plutonichealth.co"
    : "https://devenv.plutonichealth.co";
  const path = DEVELOPMENT_ROOM_PATHS[roomId];
  return path ? `${origin}${path}` : null;
}

export function prioritizeUnityRooms(rooms, preferredRoomId) {
  const preloadable = rooms.filter((room) => !room.isComingSoon && getUnityRoomBaseUrl(room.id));
  if (!preferredRoomId) return preloadable;
  return [
    ...preloadable.filter((room) => room.id === preferredRoomId),
    ...preloadable.filter((room) => room.id !== preferredRoomId),
  ];
}

export function hasUnityRoomPreloaded(roomId) {
  try {
    return window.localStorage.getItem(`${PRELOAD_STORAGE_PREFIX}${roomId}`) === "true";
  } catch {
    return false;
  }
}

export function markUnityRoomPreloaded(roomId) {
  try {
    window.localStorage.setItem(`${PRELOAD_STORAGE_PREFIX}${roomId}`, "true");
  } catch {
    // The HTTP cache still works when storage is disabled; only the visual
    // completion marker will be unavailable on the next visit.
  }
}

async function ensureUnityCacheServiceWorker() {
  if (!("serviceWorker" in navigator) || !("caches" in window)) return null;
  if (!persistentStorageRequested && navigator.storage?.persist) {
    persistentStorageRequested = navigator.storage.persist().catch(() => false);
  }
  await persistentStorageRequested;
  if (!serviceWorkerReady) {
    serviceWorkerReady = navigator.serviceWorker
      .register("/unity-preload-sw.js", { scope: "/" })
      .then(() => navigator.serviceWorker.ready)
      .catch(() => null);
  }
  await serviceWorkerReady;
  return caches.open(UNITY_CACHE_NAME);
}

function openUnityDatabase() {
  if (!("indexedDB" in window)) return Promise.resolve(null);
  if (!unityDatabaseReady) {
    unityDatabaseReady = new Promise((resolve, reject) => {
      const request = indexedDB.open(UNITY_DATABASE_NAME, UNITY_DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(UNITY_ASSET_STORE)) {
          database.createObjectStore(UNITY_ASSET_STORE, { keyPath: "url" });
        }
        if (!database.objectStoreNames.contains(UNITY_CHUNK_STORE)) {
          const chunks = database.createObjectStore(UNITY_CHUNK_STORE, { keyPath: "key" });
          chunks.createIndex("url", "url", { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return unityDatabaseReady;
}

function completeTransaction(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

async function clearIndexedDbAsset(database, url) {
  const transaction = database.transaction(
    [UNITY_ASSET_STORE, UNITY_CHUNK_STORE],
    "readwrite"
  );
  transaction.objectStore(UNITY_ASSET_STORE).delete(url);
  const cursorRequest = transaction
    .objectStore(UNITY_CHUNK_STORE)
    .index("url")
    .openCursor(IDBKeyRange.only(url));
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    cursor.delete();
    cursor.continue();
  };
  await completeTransaction(transaction);
}

async function putIndexedDbChunk(database, url, index, data) {
  const transaction = database.transaction(UNITY_CHUNK_STORE, "readwrite");
  transaction.objectStore(UNITY_CHUNK_STORE).put({
    key: `${url}::${index}`,
    url,
    index,
    data,
  });
  await completeTransaction(transaction);
}

async function getIndexedDbAsset(database, url) {
  if (!database) return null;
  return new Promise((resolve, reject) => {
    const request = database.transaction(UNITY_ASSET_STORE).objectStore(UNITY_ASSET_STORE).get(url);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

function combineChunks(chunks, byteLength) {
  const combined = new Uint8Array(byteLength);
  let offset = 0;
  chunks.forEach((chunk) => {
    combined.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return combined.buffer;
}

async function storeResponseInIndexedDb(database, url, response, onBytes, signal) {
  if (!response.ok) throw new Error(`Preload failed with status ${response.status}.`);
  await clearIndexedDbAsset(database, url);
  let chunkIndex = 0;
  let storedBytes = 0;
  let bufferedBytes = 0;
  let bufferedChunks = [];

  const flush = async () => {
    if (!bufferedBytes) return;
    await putIndexedDbChunk(
      database,
      url,
      chunkIndex,
      combineChunks(bufferedChunks, bufferedBytes)
    );
    chunkIndex += 1;
    storedBytes += bufferedBytes;
    bufferedBytes = 0;
    bufferedChunks = [];
  };

  try {
    if (!response.body?.getReader) {
      const body = new Uint8Array(await response.arrayBuffer());
      onBytes(body.byteLength);
      bufferedChunks.push(body);
      bufferedBytes = body.byteLength;
      await flush();
    } else {
      const reader = response.body.getReader();
      while (true) {
        if (signal.aborted) throw new DOMException("Preload cancelled", "AbortError");
        const { done, value } = await reader.read();
        if (done) break;
        onBytes(value.byteLength);
        bufferedChunks.push(value);
        bufferedBytes += value.byteLength;
        if (bufferedBytes >= UNITY_CHUNK_SIZE) await flush();
      }
      await flush();
    }

    const headers = Array.from(response.headers.entries())
      .filter(([name]) => !["content-encoding", "content-length"].includes(name.toLowerCase()));
    headers.push(["content-length", String(storedBytes)]);
    const transaction = database.transaction(UNITY_ASSET_STORE, "readwrite");
    transaction.objectStore(UNITY_ASSET_STORE).put({
      url,
      status: response.status,
      statusText: response.statusText,
      headers,
      chunkCount: chunkIndex,
      byteLength: storedBytes,
    });
    await completeTransaction(transaction);
  } catch (error) {
    await clearIndexedDbAsset(database, url).catch(() => {});
    throw error;
  }
}

export function extractBuildAssetUrls(setupSource, baseUrl) {
  if (!setupSource.includes("createUnityInstance")) {
    throw new Error("Unity build manifest was unavailable at the configured URL.");
  }
  const buildPathMatch = setupSource.match(/var\s+buildUrl\s*=\s*["']([^"']+)["']/);
  const buildPath = buildPathMatch?.[1] || "Build";
  const assetNames = ["loaderUrl", "dataUrl", "frameworkUrl", "codeUrl"]
    .map((key) => {
      const expression = new RegExp(`${key}\\s*:\\s*buildUrl\\s*\\+\\s*["']\\/([^"']+)["']|var\\s+${key}\\s*=\\s*buildUrl\\s*\\+\\s*["']\\/([^"']+)["']`);
      const match = setupSource.match(expression);
      return match?.[1] || match?.[2];
    })
    .filter(Boolean);

  if (assetNames.length < 4) {
    throw new Error("Unity build manifest did not contain all required assets.");
  }

  return assetNames.map((name) => new URL(`${buildPath}/${name}`, `${baseUrl}/`).href);
}

export async function isUnityRoomCached(roomId, signal) {
  const baseUrl = getUnityRoomBaseUrl(roomId);
  if (!baseUrl || new URL(baseUrl).origin !== window.location.origin) return false;

  try {
    const unityCache = await ensureUnityCacheServiceWorker();
    if (!unityCache) return false;
    const unityDatabase = await openUnityDatabase();
    const setupUrl = `${baseUrl}/TemplateData/UnitySetup.js`;
    const storedSetup = await unityCache.match(setupUrl);
    if (storedSetup) {
      const storedAssetUrls = extractBuildAssetUrls(await storedSetup.text(), baseUrl);
      const storedAssets = await Promise.all(storedAssetUrls.map(async (url) =>
        (await unityCache.match(url)) || getIndexedDbAsset(unityDatabase, url)
      ));
      if (storedAssets.every(Boolean)) {
        markUnityRoomPreloaded(roomId);
        return true;
      }
    }

    // A miss in the dedicated cache means this version still needs the normal
    // preload. Do not probe the HTTP cache with only-if-cached: Chrome forwards
    // that request through the service worker and rejects fetch() on a miss.
    return false;
  } catch (error) {
    if (error.name === "AbortError") throw error;
    return false;
  }
}

export async function preloadUnityRoom(roomId, onProgress, signal) {
  const baseUrl = getUnityRoomBaseUrl(roomId);
  if (!baseUrl) throw new Error(`No Unity WebGL build is configured for ${roomId}.`);

  const unityCache = await ensureUnityCacheServiceWorker();
  if (!unityCache) throw new Error("Persistent Unity caching is not available in this browser.");
  const unityDatabase = await openUnityDatabase();
  if (!unityDatabase) throw new Error("Persistent Unity asset storage is not available in this browser.");
  const setupUrl = `${baseUrl}/TemplateData/UnitySetup.js`;
  const setupResponse = await fetch(setupUrl, { cache: "no-store", signal });
  if (!setupResponse.ok) throw new Error(`Unable to read the Unity build manifest for ${roomId}.`);
  const setupSource = await setupResponse.clone().text();
  await unityCache.put(setupUrl, setupResponse);
  const assetUrls = extractBuildAssetUrls(setupSource, baseUrl);
  const sizes = await Promise.all(assetUrls.map(async (url) => {
    const response = await fetch(url, { method: "HEAD", cache: "no-store", signal });
    return response.ok ? Number(response.headers.get("content-length")) || 0 : 0;
  }));
  const totalBytes = sizes.reduce((sum, size) => sum + size, 0);
  let loadedBytes = 0;
  const reportBytes = (bytes) => {
    loadedBytes += bytes;
    if (totalBytes > 0) onProgress(Math.min(loadedBytes / totalBytes, 0.99));
  };

  // Chromium's Cache Storage can reject individual Unity files hundreds of MB
  // in size. Store them as small IndexedDB chunks that the service worker can
  // stream back to Unity without rebuilding the whole file in memory.
  for (const url of assetUrls) {
    const response = await fetch(url, { cache: "no-store", signal });
    await storeResponseInIndexedDb(unityDatabase, url, response, reportBytes, signal);
  }
  onProgress(1);
}
